import axios from 'axios';
import { LOCAL_STORAGE_KEYS } from "../utils/constants";
import { toastWarning } from "../utils/utils";
import { refreshToken } from "../services/auth.service";
import { ECC_cipher_helper } from "../utils/ecc_cipher_util";
import { COLOR_MODE_STORAGE_KEY } from '../context/colorMode/colorMode.context';

axios.defaults.headers.post['Content-Type'] = 'application/json';
const base_uri = process.env.REACT_APP_MYDQ_API;

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(promise => {
        if (error) {
            promise.reject(error);
        } else {
            promise.resolve(token);
        }
    });

    failedQueue = [];
};

function invokeApiWihToken(reqParams, token) {
    const { headers } = reqParams;
    const modifiedHeaders = { ...headers, 'Authorization': `Bearer ${token}` }
    const modifiedParams = { ...reqParams, headers: modifiedHeaders };
    return invokeApi(modifiedParams);
}

function retryRequestWithRefresh(reqParams) {
    return new Promise((resolve, reject) => {
        refreshToken() // Make a request to refresh the token
            .then(response => {
                const { auth_token } = response.data;
                localStorage.setItem(LOCAL_STORAGE_KEYS.auth_token, JSON.stringify(auth_token));
                // Only the token is replaced. The stored user stays the one login
                // gave: the refresh endpoint's user lacks the workspace's plan for a
                // network, school or teacher (dq_pro_plan null), and storing it made
                // a hard refresh drop their menu and 404 My Schools until the next
                // login (LA-PW,SNW,SW,TW-20Oct-03). The page in memory never used it.
                processQueue(null, auth_token); // Handle queued requests
                resolve(invokeApiWihToken(reqParams, auth_token));
            })
            .catch(err => {
                processQueue(err, null); // Handle queued requests
                reject(err);
            })
            .finally(() => {
                isRefreshing = false; // Reset flag
            });
    });
}

function putRequestInQueue(reqParams) {
    return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject })
    })
        .then(token => invokeApiWihToken(reqParams, token)) // Retry request with new token
        .catch(err => Promise.reject(err));
}

function forceLogout() {
    // The light/dark choice is a personal, per-browser setting that is meant to
    // survive logout (see colorMode.context.jsx) — keep it across the wipe.
    const colorMode = localStorage.getItem(COLOR_MODE_STORAGE_KEY);
    localStorage.clear();
    if (colorMode) localStorage.setItem(COLOR_MODE_STORAGE_KEY, colorMode);
    window.location.reload();
}

function getRefreshToken() {
    return JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.refresh_token));
}

/**
 * Browser offline or connection dropped (ERR_INTERNET_DISCONNECTED).
 *
 * A RESPONSE PROVES THE NETWORK WORKED. If the server answered — even with 419,
 * 401 or 500 — the request reached it and came back, so this cannot be a
 * connectivity failure no matter what `navigator.onLine` claims.
 *
 * That ordering used to be reversed, and it broke session refresh (PBDQ-154):
 * `navigator.onLine` was consulted FIRST, so whenever the browser spuriously
 * reported offline, a genuine 419 "token expired" was rethrown as
 * "NETWORK ERROR : No internet connection". The 419 branch below never ran, the
 * token was never refreshed, and the user was told to check their internet while
 * the real problem was an expired session.
 *
 * `navigator.onLine` is only a hint: it reports whether the OS has a network
 * interface, not whether anything is reachable. It flips spuriously on VPN
 * changes, Wi-Fi handoff and sleep/wake. So it is consulted LAST, and only when
 * there is no response to contradict it.
 */
function isNetworkFailure(error) {
    // The server answered — definitively not a connectivity problem.
    if (error && error.response) return false;

    const msg = String((error && error.message) || '').toLowerCase();
    if (msg.includes('network') || (error && error.code === 'ERR_NETWORK')) return true;

    // No response and no network-shaped error: fall back to the browser's hint.
    return typeof navigator !== 'undefined' && !navigator.onLine;
}

export async function invokeApi(reqParams, use_e2ee = false) {
    const {
        path,
        method = 'GET',
        headers = {},
        queryParams = {},
        postData = {},
        ...rest
    } = reqParams;

    let reqObj = {
        method: method,
        url: base_uri + path,
        headers: headers,
        ...rest
    };
    reqObj['params'] = queryParams;

    if (method === 'POST') {
        let e2ee_payload = false;
        let e2ee_obj_str, e2ee_obj_keypair;
        if (use_e2ee) {
            const e2ee_obj_b64 = localStorage.getItem(LOCAL_STORAGE_KEYS.e2ee_krypto_pair_obj);
            if (e2ee_obj_b64) e2ee_obj_str = atob(e2ee_obj_b64);
            if (e2ee_obj_str) e2ee_obj_keypair = JSON.parse(e2ee_obj_str);
        }
        if (e2ee_obj_keypair && e2ee_obj_keypair.pub) {
            e2ee_payload = true;

            // encrypt using ECC-keys
            const publicKey = JSON.parse(atob(e2ee_obj_keypair.pub))
            const payloadString = JSON.stringify(postData);
            const payload = await ECC_cipher_helper.encryptData(payloadString, publicKey);

            reqObj['data'] = {
                payload,
                e2ee_payload
            }
        } else {
            reqObj['data'] = postData
        }
    }

    let results = undefined;

    try {
        results = await axios(reqObj);

        /**
         * A completed round trip is proof the connection works.
         *
         * The offline banner is driven by `navigator.onLine`, which the browser
         * sometimes reports incorrectly — that is how PBDQ-154 was captured with
         * "You're offline" on screen while the very same page was receiving HTTP
         * 419 responses from the API. Announcing a real success lets the banner
         * correct itself instead of sitting there contradicting the network tab.
         */
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('api-reachable'));
        }

        if (results.data.data && results.data.data.e2ee_data) {
            // need to decrypt the response
            let e2ee_obj_str, e2ee_obj_keypair;
            const e2ee_obj_b64 = localStorage.getItem(LOCAL_STORAGE_KEYS.e2ee_krypto_pair_obj);
            if (e2ee_obj_b64) e2ee_obj_str = atob(e2ee_obj_b64);
            if (e2ee_obj_str) e2ee_obj_keypair = JSON.parse(e2ee_obj_str);
            if (e2ee_obj_keypair && e2ee_obj_keypair.pvt) {
                const privateKey = JSON.parse(atob(e2ee_obj_keypair.pvt));
                const cipher_data = results.data.data.secure_data;
                const responseString = await ECC_cipher_helper.decryptData(cipher_data, privateKey);
                const responseData = JSON.parse(responseString);
                if (Array.isArray(responseData)) results.data.data = [...responseData];
                else results.data.data = { ...responseData };
                if (!use_e2ee) console.log(`QA:MODE: path: ${path} --> E2EE data received:`, results.data.data);
            } else {
                toastWarning(`System security protocols are update! 
                Your current session is forced logged out!
                Please re-login to adopt the improved system.`);

                setTimeout(() => {
                    forceLogout();
                }, 4000);

                return false;
            }
        }
        return results.data;
    } catch (error) {
        console.log('invoke API :: Error: ', error);

        if (isNetworkFailure(error)) {
            throw new Error('NETWORK ERROR : No internet connection');
        }

        const status = error.response?.status;

        if (status === 499) throw new Error('Permission denied');

        if (status === 401) {
            forceLogout();
            throw new Error('Unauthorized');
        }

        if (status === 419) {
            if (!getRefreshToken()) {
                forceLogout();
                throw new Error('Unauthorized');
            }
            if (isRefreshing) return putRequestInQueue(reqParams);
            isRefreshing = true;
            return retryRequestWithRefresh(reqParams);
        }

        // handover: rejections use {error:true,error_message}; exceptions use {message}.
        // The thrown Error carries status/body/errorField so callers can tell them apart.
        const body = error.response?.data;
        const msg = body?.message || body?.error_message;
        const errorField = body?.message ? 'message' : (msg ? 'error_message' : null);
        const errorMeta = { status, body, errorField };

        if (!msg) {
            toastWarning('NETWORK ERROR : Some Thing Went Wrong!');
            throw Object.assign(new Error('NETWORK ERROR : Some Thing Went Wrong!'), errorMeta);

        } else {
            // "NOK:" errors are surfaced in a dialog (GlobalErrorDialog) instead of a
            // toast — same convention as my-dq-analytics. Everything else stays a toast.
            if (String(msg).toUpperCase().includes('NOK')) {
                const cleanMsg = String(msg).replace(/NOK\s*:?/i, '').trim();
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('nok-error', { detail: cleanMsg }));
                }
            } else {
                toastWarning(msg);
            }
            throw Object.assign(new Error(msg), errorMeta);
        }
    }
}